// FeaturedStrategyCard component - featured strategy card with three-column layout
// Displays: left (emoji, name, description, pills, button), center (features), right (case study)
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { Button } from '@design-system/components/button/button';
import { Typography } from '@design-system/components/typography/typography';
import { StrategyVM, CaseStudyVM } from '../../view-model/Strategy.viewModel';
import { StrategyCaseStudyCard } from './StrategyCaseStudyCard';
import * as styles from './FeaturedStrategyCard.css';

export type FeaturedStrategyCardProps = {
  strategy: StrategyVM;
  caseStudy: CaseStudyVM | null;
  onSelect: (strategyId: string) => void;
};

const MAX_FEATURES_DISPLAY = 6;

export const FeaturedStrategyCard = ({
  strategy,
  caseStudy,
  onSelect,
}: FeaturedStrategyCardProps) => {
  const displayFeatures = strategy.features.slice(0, MAX_FEATURES_DISPLAY);

  const handleSelect = () => {
    onSelect(strategy.id);
  };

  return (
    <div
      className={styles.card}
      data-testid={`featured-strategy-card-${strategy.id}`}
    >
      {/* Left Section: Emoji, Name, Description, Pills, Button */}
      <div className={styles.leftSection}>
        {strategy.superTitle && (
          <span className={styles.superTitle}>{strategy.superTitle}</span>
        )}

        <Typography
          as="h3"
          variant="title"
          size="large"
          className={styles.name}
        >
          {strategy.name}
        </Typography>

        {strategy.tagline && (
          <Typography variant="body" size="medium" className={styles.tagline}>
            {strategy.tagline}
          </Typography>
        )}

        {strategy.pills.length > 0 && (
          <div className={styles.pillsContainer}>
            {strategy.pills.map((pill, index) => (
              <span key={index} className={styles.pill}>
                {pill}
              </span>
            ))}
          </div>
        )}

        <Button
          variant="filled"
          color="neutral"
          size="large"
          className={styles.button}
          onClick={handleSelect}
          testId={`featured-strategy-select-${strategy.id}`}
        >
          Choose this strategy
        </Button>
      </div>

      {/* Center Section: Features List */}
      {displayFeatures.length > 0 && (
        <div className={styles.centerSection}>
          <ul className={styles.featureList}>
            {displayFeatures.map((feature, index) => (
              <li key={index} className={styles.featureItem}>
                <FontAwesomeIcon icon={faCheck} className={styles.checkIcon} />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Right Section: Embedded Case Study */}
      {caseStudy && (
        <div className={styles.rightSection}>
          <Typography
            variant="body"
            size="small"
            fontWeight="medium"
            className={styles.caseStudyHeading}
          >
            {strategy.name} Case Study
          </Typography>
          <div className={styles.caseStudyCardWrapper}>
            <StrategyCaseStudyCard
              caseStudy={caseStudy}
              testId={`embedded-case-study-${caseStudy.id}`}
            />
          </div>
        </div>
      )}
    </div>
  );
};
